import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import currency from "currency.js";
import moment from "moment";
import bkstApi from "../../api";
import { UserContext } from "../../context/UserContext";
import Option, { OPTION_GRID_TILE_SX } from "../../product/component/Option";
import StripePaymentElement from "../../common/component/StripePaymentElement";
import FinixPaymentElement from "../../common/component/FinixPaymentElement";
import PaymentStatus from "../../common/screen/PaymentStatus";
import PaymentSuccessScreen from "../../common/screen/PaymentSuccessScreen";
import CashEntry from "./CashEntry";
import NumericTextField from "../../common/component/numeric/NumericTextField";
import PaymentMethodIcon from "../../common/component/PaymentMethodIcon";
import { getOtherPosPaymentMethods, POS_OTHER_PAYMENT_UI_KEY } from "../../common/util";
import AddCardIcon from "@mui/icons-material/AddCard";
import ContactlessOutlinedIcon from "@mui/icons-material/ContactlessOutlined";
import DialpadOutlinedIcon from "@mui/icons-material/DialpadOutlined";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

const PAYMENT_OPTION_ICON_SX = { color: "#777" };

const PAYMENT_OPTION_ICONS = {
  cf: <AddCardIcon sx={PAYMENT_OPTION_ICON_SX} />,
  cp: <ContactlessOutlinedIcon sx={PAYMENT_OPTION_ICON_SX} />,
  moto: <DialpadOutlinedIcon sx={PAYMENT_OPTION_ICON_SX} />,
  cash: <PaymentMethodIcon paymentType="offline" />,
  other: <OpenInNewOutlinedIcon sx={PAYMENT_OPTION_ICON_SX} />,
};

const ASYNC_POLL_MESSAGES = {
  cf: "Confirming card on file payment…",
  moto: "Confirming virtual terminal payment…",
  cp: "Confirming card reader payment…",
};

const FINIX_CARD_PAYMENT_METHODS = new Set(["moto", "cf", "cp"]);

export default function CollectBalancePayment(props) {
  const {
    orderId: orderIdProp,
    paymentIntentId,
    onPaymentSuccess,
    onComplete,
    onOpenChange,
    onOrderLoaded,
    hideTrigger = false,
  } = props;
  const { user } = useContext(UserContext);

  const isOpenControlled = props.open !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isOpenControlled ? props.open : internalOpen;

  const setOpen = (next) => {
    if (!isOpenControlled) {
      setInternalOpen(next);
    }
    onOpenChange?.(next);
  };

  const [order, setOrder] = useState(null);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [loadError, setLoadError] = useState("");

  const getDefaultPaymentMethod = (o) => {
    if (o?.stripeId || o?.paymentMethodId) {
      return "cf";
    }
    if (user.config?.readerId) {
      return "cp";
    }
    return "moto";
  };

  const [amountType, setAmountType] = useState("full");
  const [customAmount, setCustomAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("moto");
  const [cash, setCash] = useState("");
  const [otherPaymentMethod, setOtherPaymentMethod] = useState("");
  const [referenceId, setReferenceId] = useState("");
  const [errors, setErrors] = useState([]);
  const [status, setStatus] = useState("");
  const [pollingMessage, setPollingMessage] = useState("");
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState("");
  const customAmountInputRef = useRef(null);

  const resolveOrderId = () => {
    if (orderIdProp) {
      return Promise.resolve(orderIdProp);
    }
    if (!paymentIntentId) {
      return Promise.reject(new Error("Order not found"));
    }

    const deadline = Date.now() + 15000;
    const poll = () =>
      bkstApi(`/my/payment-intent/${paymentIntentId}/order`)
        .then((res) => res.data.orderId)
        .catch((e) => {
          if (e.response?.status !== 404 || Date.now() > deadline) {
            throw e;
          }
          return new Promise((resolve) => setTimeout(resolve, 1000)).then(poll);
        });

    return poll();
  };

  const loadOrder = () => {
    setLoadingOrder(true);
    setLoadError("");
    resolveOrderId()
      .then((id) => bkstApi(`/my/order/${id}`))
      .then((res) => {
        setOrder(res.data);
        setPaymentMethod(getDefaultPaymentMethod(res.data));
        onOrderLoaded?.(res.data.id);
      })
      .catch((e) => {
        setOrder(null);
        setLoadError(e.response?.data?.message || e.message || "Unable to load order");
      })
      .finally(() => {
        setLoadingOrder(false);
      });
  };

  useEffect(() => {
    if (open) {
      loadOrder();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load when dialog opens
  }, [open]);

  useEffect(() => {
    if (amountType !== "full") {
      customAmountInputRef.current?.focus();
    }
  }, [amountType]);

  const hasCardOnFile = Boolean(
    order?.stripeId || order?.paymentMethodId || order?.paymentType === "deferred"
  );
  const balanceDue = order ? +order.payment.due : 0;
  const chargeAmount = amountType === "full" ? balanceDue : +customAmount || 0;

  const paymentOptions = [
    ...(hasCardOnFile ? [{ key: "cf", title: "Card on File" }] : []),
    ...(user.config?.readerId ? [{ key: "cp", title: "Card Reader" }] : []),
    { key: "moto", title: "Virtual Terminal" },
    { key: "cash", title: "Cash" },
    { key: POS_OTHER_PAYMENT_UI_KEY, title: "Other" },
  ];
  const otherPaymentMethods = getOtherPosPaymentMethods(user.config);

  const selectPaymentMethod = (key) => {
    setPaymentMethod(key);
    if (key !== POS_OTHER_PAYMENT_UI_KEY) {
      setOtherPaymentMethod("");
      setReferenceId("");
    }
  };

  const resetForm = () => {
    setAmountType("full");
    setCustomAmount("");
    setPaymentMethod(getDefaultPaymentMethod(order));
    setCash("");
    setOtherPaymentMethod("");
    setReferenceId("");
    setErrors([]);
    setStatus("");
    setResult("");
    setPollingMessage("");
    setProcessing(false);
  };

  const buildPaymentResult = (data = {}, methodKey) => ({
    ...data,
    orderId: data.orderId || order.id,
    email: order.customer?.email,
    phone: order.customer?.phone,
    cash: methodKey === "cash" ? cash : "",
    // Snapshot until order fetch returns so full pays don't briefly show Take Another
    remainingDue: Math.max(0, +currency(balanceDue).subtract(chargeAmount)),
    fulfillmentType: order.fulfillment?.type || "",
  });

  const refreshOrderAfterPayment = (orderId) => {
    if (!orderId) {
      return;
    }
    bkstApi(`/my/order/${orderId}`)
      .then((res) => {
        setOrder(res.data);
        setResult((prev) => ({
          ...prev,
          remainingDue: +res.data.payment?.due || 0,
          email: res.data.customer?.email || prev.email,
          phone: res.data.customer?.phone || prev.phone,
          fulfillmentType: res.data.fulfillment?.type || prev.fulfillmentType || "",
        }));
      })
      .catch(() => {});
  };

  const notifyPaymentSuccess = (data, methodKey) => {
    const paymentResult = buildPaymentResult(data, methodKey);
    setResult(paymentResult);
    setStatus("success");
    refreshOrderAfterPayment(paymentResult.orderId);
    const callback = onPaymentSuccess || onComplete;
    if (callback) {
      callback(paymentResult);
    }
  };

  const takeAnotherPayment = () => {
    resetForm();
  };

  const handleClose = async () => {
    setOpen(false);
    setOrder(null);
    setLoadError("");
    resetForm();
  };

  const validateAmount = () => {
    const arr = [];
    if (amountType === "custom") {
      if (!customAmount || +customAmount <= 0) {
        arr.push("Enter a valid amount");
      } else if (+customAmount > balanceDue) {
        arr.push("Amount cannot exceed balance due");
      } else if (+customAmount < 0.5) {
        arr.push("Charge amount should be $0.50 or more");
      }
    }
    return arr;
  };

  const waitForTransaction = (paymentIntentId) => {
    const deadline = Date.now() + 30000;

    const poll = () =>
      bkstApi
        .get(`/my/order/${order.id}/transaction/${paymentIntentId}`)
        .then(() => {})
        .catch((e) => {
          if (e.response?.status !== 404) {
            throw e;
          }
          if (Date.now() > deadline) {
            throw new Error("Payment is still processing. Please refresh the page.");
          }
          return new Promise((resolve) => setTimeout(resolve, 2000)).then(poll);
        });

    return poll();
  };

  const completeAsyncPayment = (paymentIntentId, methodKey, paymentData = {}) => {
    setPollingMessage(ASYNC_POLL_MESSAGES[methodKey] || "Confirming payment…");
    setStatus("polling");

    return waitForTransaction(paymentIntentId)
      .then(() => {
        notifyPaymentSuccess({ ...paymentData, paymentIntentId }, methodKey);
      })
      .catch((e) => {
        setStatus("");
        setErrors([e.response?.data?.message || e.message]);
      });
  };

  const sendBalanceToCardReader = () => {
    const arr = validateAmount();
    setErrors(arr);
    if (arr.length > 0) {
      return;
    }

    return bkstApi
      .post(`/my/order/${order.id}/payment`, {
        amount: chargeAmount,
        paymentMethod: "cp",
      })
      .then((res) => {
        const paymentIntentId = res.data.paymentIntentId;
        return bkstApi.post(`/my/reader/${user.config.readerId}/payment/${paymentIntentId}`, {
          process_config: {
            skip_tipping: true,
          },
        });
      });
  };

  const collectPaymentUrl = (methodKey) => {
    if (methodKey === "cf" && order?.paymentType === "deferred") {
      return `/my/order/${order.id}/payment`;
    }
    if (user.processor === "finix" && FINIX_CARD_PAYMENT_METHODS.has(methodKey)) {
      return `/my/finix/order/${order.id}/payment`;
    }
    return `/my/order/${order.id}/payment`;
  };

  const submitPayment = (payload) => {
    const arr = validateAmount();
    setErrors(arr);
    if (arr.length > 0) {
      return Promise.reject(new Error(arr.join(". ")));
    }

    setProcessing(true);

    const requestBody = {
      amount: chargeAmount,
      ...payload,
      receiptPrinterId: user.config?.receiptPrinterId || "",
    };
    if (user.processor === "finix" && FINIX_CARD_PAYMENT_METHODS.has(payload.paymentMethod)) {
      requestBody.idempotencyId = payload.idempotencyId || crypto.randomUUID();
    }
    if (user.processor === "finix" && payload.paymentMethod === "moto") {
      delete requestBody.paymentMethodId;
    }

    return bkstApi
      .post(collectPaymentUrl(payload.paymentMethod), requestBody)
      .then((res) => {
        if (res.data.paymentIntentId) {
          setProcessing(false);
          return completeAsyncPayment(res.data.paymentIntentId, payload.paymentMethod, res.data);
        }

        notifyPaymentSuccess(res.data, payload.paymentMethod);
        setProcessing(false);
      })
      .catch((e) => {
        setProcessing(false);
        setErrors([e.response?.data?.message || e.message]);
        throw e;
      });
  };

  const customerName = order?.customer
    ? `${order.customer.firstName || ""} ${order.customer.lastName || ""}`.trim()
    : "";

  const orderDate = order?.fulfillment?.date
    ? moment(order.fulfillment.date).format("ddd, MMM D, YYYY")
    : "";

  return (
    <>
      {!hideTrigger && (
        <Button
          color="primary"
          variant="contained"
          onClick={() => {
            setOpen(true);
          }}
          disableElevation
          fullWidth
        >
          Take Payment
        </Button>
      )}

      {open && (loadingOrder || (!order && !loadError)) ? (
        <Dialog fullScreen open={open} scroll="paper" onClose={handleClose}>
          <DialogContent>
            <Box
              py={4}
              sx={{
                minHeight: "60vh",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress size={56} />
              <Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
                Loading order…
              </Typography>
            </Box>
          </DialogContent>
        </Dialog>
      ) : open && loadError ? (
        <Dialog fullScreen open={open} scroll="paper" onClose={handleClose}>
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              margin: 0,
              backgroundColor: "#DEDEDE",
              padding: 0,
            }}
          >
            <IconButton onClick={handleClose}>
              <ArrowBackIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box py={4} sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Alert severity="error">{loadError}</Alert>
              <Button sx={{ mt: 2 }} variant="contained" onClick={loadOrder}>
                Retry
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
      ) : status === "success" ? (
        <PaymentSuccessScreen
          open={open}
          onClose={handleClose}
          result={result}
          onDone={handleClose}
          onCollectPayment={+result.remainingDue > 0 ? takeAnotherPayment : undefined}
        />
      ) : status === "polling" ? (
        <Dialog fullScreen open={open} scroll="paper" onClose={handleClose}>
          <DialogContent>
            <Box
              py={4}
              sx={{
                minHeight: "60vh",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                px: 2,
              }}
            >
              <CircularProgress size={56} sx={{ mb: 3 }} />
              <Typography variant="h6" gutterBottom>
                {currency(chargeAmount).format()}
              </Typography>
              <Typography variant="body1" color="textSecondary" sx={{ maxWidth: 360 }}>
                {pollingMessage}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                This may take a few seconds.
              </Typography>
            </Box>
          </DialogContent>
        </Dialog>
      ) : order ? (
        <Dialog fullScreen open={open} scroll="paper" onClose={handleClose}>
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              margin: 0,
              backgroundColor: "#DEDEDE",
              padding: 0,
            }}
          >
            <IconButton onClick={handleClose}>
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ py: 1.5 }}>
              <Typography variant="h5">{customerName || "Customer"}</Typography>
              <Typography variant="overline" color="textSecondary" display="block">
                {[orderDate, ""].filter(Boolean).join(" • ")}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ backgroundColor: "#F7F7F7" }}>
            <Container maxWidth="xl">
              <Box py={2}>
                {errors.length > 0 && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {errors.join(". ")}
                  </Alert>
                )}

                <Grid container spacing={4}>
                  <Grid item sm={4} xs={12}>
                    <Paper elevation={0}>
                      <Box p={2}>
                        <Typography variant="h6" color="textSecondary" gutterBottom>
                          Payment Summary
                        </Typography>
                        <Grid container spacing={1}>
                          <Grid item xs={7}>
                            <Typography variant="overline" color="textSecondary">
                              Order Total
                            </Typography>
                          </Grid>
                          <Grid item xs={5} textAlign="right">
                            <Typography variant="overline">
                              {currency(order.payment.total).format()}
                            </Typography>
                          </Grid>
                          <Grid item xs={7}>
                            <Typography variant="overline" color="textSecondary">
                              Amount Paid
                            </Typography>
                          </Grid>
                          <Grid item xs={5} textAlign="right">
                            <Typography variant="overline">
                              {currency(order.payment.paid).format()}
                            </Typography>
                          </Grid>
                          <Grid item xs={7}>
                            <Typography variant="overline" color="textSecondary">
                              Balance Due
                            </Typography>
                          </Grid>
                          <Grid item xs={5} textAlign="right">
                            <Typography variant="body1" sx={{ fontWeight: "700" }}>
                              {currency(balanceDue).format()}
                            </Typography>
                          </Grid>
                        </Grid>

                        <Box mt={3} pt={2} sx={{ borderTop: "1px solid #ececec" }}>
                          <Typography variant="h6" color="textSecondary" gutterBottom>
                            Amount to Charge
                          </Typography>
                          <FormControl component="fieldset" fullWidth>
                            <RadioGroup
                              value={amountType}
                              onChange={(e) => {
                                const value = e.target.value;
                                setAmountType(value);
                                if (value === "full") {
                                  setCustomAmount("");
                                }
                              }}
                            >
                              <FormControlLabel
                                value="full"
                                control={<Radio />}
                                label={
                                  <Typography variant="body1">
                                    {currency(balanceDue).format()} (balance due)
                                  </Typography>
                                }
                              />
                              <FormControlLabel
                                value="custom"
                                control={<Radio />}
                                label={<Typography variant="body1">Custom Amount</Typography>}
                              />
                            </RadioGroup>
                          </FormControl>
                          {amountType === "custom" && (
                            <Box mt={2}>
                              <NumericTextField
                                inputRef={customAmountInputRef}
                                allowNegative={false}
                                decimalScale={2}
                                isAllowed={(values) => {
                                  const { value } = values;
                                  return value <= balanceDue;
                                }}
                                value={customAmount}
                                onChange={(value) => {
                                  setCustomAmount(value);
                                }}
                                label="Amount"
                                variant="outlined"
                                helperText={`Max amount is ${currency(balanceDue).format()}`}
                              />
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item sm={8} xs={12}>
                    <Box mb={2} sx={{ display: "flex", gap: 1 }}>
                      {paymentOptions.map(({ key, title }) => (
                        <Box key={key} sx={{ flex: "1 1 0", minWidth: 0 }}>
                          <Option
                            title={title}
                            titleIcon={PAYMENT_OPTION_ICONS[key]}
                            sx={OPTION_GRID_TILE_SX}
                            onClick={() => selectPaymentMethod(key)}
                            selected={paymentMethod === key}
                          />
                        </Box>
                      ))}
                    </Box>

                    {paymentMethod === "cf" && (
                      <Box>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Charge the customer's card on file.
                        </Typography>
                        <Button
                          fullWidth
                          color="primary"
                          variant="contained"
                          sx={{ mt: 1, fontSize: "1.25rem", padding: "1rem" }}
                          disabled={processing || chargeAmount <= 0}
                          onClick={() => submitPayment({ paymentMethod: "cf" })}
                        >
                          {currency(chargeAmount).format()} | CHARGE CARD ON FILE
                        </Button>
                      </Box>
                    )}

                    {paymentMethod === "cp" && (
                      <Box>
                        {user.config?.readerId ? (
                          <>
                            <PaymentStatus
                              sx={{ fontSize: "1.25rem", padding: "1rem" }}
                              amount={chargeAmount}
                              readerId={user.config.readerId}
                              closeOnSuccess
                              onSubmit={sendBalanceToCardReader}
                              onSuccess={(o) => {
                                completeAsyncPayment(o.paymentIntentId, "cp", {
                                  orderId: order.id,
                                  paymentIntentId: o.paymentIntentId,
                                });
                              }}
                            />
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              textAlign="center"
                              mt={1}
                            >
                              Send a payment request to the card reader. Ask the customer to verify
                              the amount and tap or insert their card.
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2" color="error" textAlign="center">
                            No card reader configured for this account.
                          </Typography>
                        )}
                      </Box>
                    )}

                    {paymentMethod === "moto" && (
                      <Box>
                        {user.processor === "finix" ? (
                          <FinixPaymentElement
                            formElementId="finix-collect-balance-form"
                            sx={{ fontSize: "1.25rem", padding: "1rem" }}
                            validate={validateAmount}
                            chargePaymentMethod={(token, _amount, idempotencyId) =>
                              submitPayment({
                                paymentMethod: "moto",
                                token,
                                idempotencyId,
                              })
                            }
                            amount={chargeAmount}
                          />
                        ) : (
                          <Elements stripe={stripePromise}>
                            <StripePaymentElement
                              sx={{ fontSize: "1.25rem", padding: "1rem" }}
                              validate={validateAmount}
                              chargePaymentMethod={(paymentMethodId) =>
                                submitPayment({
                                  paymentMethod: "moto",
                                  paymentMethodId,
                                })
                              }
                              amount={chargeAmount}
                              name={customerName}
                              phone={order.customer?.phone}
                            />
                          </Elements>
                        )}
                        <Typography variant="body2" color="textSecondary" textAlign="center" mt={1}>
                          Enter the customer's card details to collect payment.
                        </Typography>
                      </Box>
                    )}

                    {paymentMethod === "cash" && (
                      <Box>
                        <Paper elevation={0} sx={{ backgroundColor: "#F7F7F7" }}>
                          <CashEntry
                            amountDue={currency(chargeAmount).intValue}
                            onChange={(o) => setCash(o)}
                          />
                        </Paper>
                        <Button
                          sx={{ mt: 2, fontSize: "1.25rem", padding: "1rem" }}
                          color="primary"
                          variant="contained"
                          onClick={() =>
                            submitPayment({
                              paymentMethod: "cash",
                              tendered: cash.tendered,
                            })
                          }
                          fullWidth
                          disabled={
                            processing ||
                            chargeAmount <= 0 ||
                            (+chargeAmount > 0 && (!cash || +cash?.tendered < +chargeAmount))
                          }
                        >
                          {currency(chargeAmount).format()} | CASH PAYMENT
                        </Button>
                      </Box>
                    )}

                    {paymentMethod === POS_OTHER_PAYMENT_UI_KEY && (
                      <Box>
                        <Paper
                          elevation={0}
                          sx={{
                            borderRadius: 1,
                            p: 2,
                            mb: 2,
                            bgcolor: "#fff",
                          }}
                        >
                          <Typography
                            variant="overline"
                            color="textSecondary"
                            display="block"
                            gutterBottom
                          >
                            Other payment type
                          </Typography>
                          <FormControl component="fieldset" fullWidth sx={{ mb: 2 }}>
                            <RadioGroup
                              value={otherPaymentMethod}
                              onChange={(e) => setOtherPaymentMethod(e.target.value)}
                            >
                              {Object.entries(otherPaymentMethods)
                                .sort(([, a], [, b]) => a.title.localeCompare(b.title))
                                .map(([key, { title }]) => (
                                  <FormControlLabel
                                    key={key}
                                    value={key}
                                    control={<Radio />}
                                    label={<Typography variant="body1">{title}</Typography>}
                                  />
                                ))}
                            </RadioGroup>
                          </FormControl>
                          <TextField
                            fullWidth
                            label="Reference ID (optional)"
                            value={referenceId}
                            onChange={(e) => setReferenceId(e.target.value)}
                          />
                        </Paper>
                        <Button
                          sx={{ fontSize: "1.25rem", padding: "1rem" }}
                          color="primary"
                          variant="contained"
                          onClick={() =>
                            submitPayment({
                              paymentMethod: otherPaymentMethod,
                              referenceId: referenceId.trim() || undefined,
                            })
                          }
                          fullWidth
                          disabled={processing || chargeAmount <= 0 || !otherPaymentMethod}
                        >
                          {currency(chargeAmount).format()} |{" "}
                          {otherPaymentMethods[otherPaymentMethod]?.label || "RECORD PAYMENT"}
                        </Button>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Container>
            {processing && (
              <Backdrop open={true} sx={{ zIndex: 1000, color: "#fff" }}>
                <CircularProgress color="inherit" />
              </Backdrop>
            )}
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
}
